import React from "react";
import { Trophy, CheckCircle2, Crown, Star, Calendar, Award } from "lucide-react";
import PageShell from "@/components/PageShell";

const RULES = [
  "Open only to students of Grades 5 to 8 across participating regions.",
  "Entries are individual — each student shares their own story.",
  "Please share original work created by the student.",
  "Stories should be inspired by a school trip (with Sprouts Edu Tours or any school-organised travel).",
  "We love positive, thoughtful storytelling.",
  "Entries can be a travel story, photography, video story, journal or creative travel expression — whichever feels right.",
  "School registrations deadline: 31 July 2026.",
  "Regional champions and category winners will be celebrated in September 2026.",
  "Every winning category receives a trophy, certificate, recognition and a cash prize.",
  "Every valid entry receives a Participation Certificate.",
  "Selected stories may receive recognition and visibility through Times NIE and Sprouts Edu Tours platforms.",
  "The judges' shortlist is final, but every story is read with care.",
];

const REGIONAL_CHAMPIONS = [
  "Telangana",
  "Andhra Pradesh",
  "Tamil Nadu",
  "Karnataka",
  "Maharashtra",
  "Delhi NCR",
  "Chandigarh & Tricity",
];

const CATEGORY_AWARDS = [
  { title: "Best Travel Story", color: "#3AB0FF" },
  { title: "Best Travel Photographer", color: "#FF6B6B" },
  { title: "Best Creativity", color: "#FFD166" },
  { title: "Most Inspiring Story", color: "#06B6D4" },
  { title: "Best School Participation", color: "#34D399" },
  { title: "Radio Mirchi Special", color: "#E14F4F" },
  { title: "Sprouts Special Recognition", color: "#A78BFA" },
  { title: "Participation Certificate", color: "#8B5CF6" },
];

export default function RulesPrizes() {
  return (
    <PageShell testid="rules-screen">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <p className="text-[11px] font-extrabold tracking-[0.25em] uppercase text-sprouts-secondary mb-2">
          The Championship
        </p>
        <h1 className="text-3xl sm:text-4xl font-black text-sprouts-text">Rules & Recognition</h1>
        <p className="text-base text-sprouts-muted mt-2 max-w-3xl">
          Everything you need to know about taking part in India's Young Travel Storytelling
          Championship 2026.
        </p>

        {/* Important Dates */}
        <section className="mt-10" data-testid="rules-important-dates">
          <div className="flex items-center gap-2 mb-5">
            <Calendar size={20} className="text-sprouts-primary" />
            <h2 className="text-2xl font-extrabold text-sprouts-text">Important Dates</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl p-5 border border-sprouts-border shadow-soft">
              <p className="text-xs font-extrabold tracking-widest uppercase text-sprouts-muted">
                Last date for entry submission
              </p>
              <p className="text-2xl font-black text-sprouts-text mt-1.5">10 September 2026</p>
            </div>
            <div className="bg-white rounded-2xl p-5 border border-sprouts-border shadow-soft">
              <p className="text-xs font-extrabold tracking-widest uppercase text-sprouts-muted">
                Winners will be announced by
              </p>
              <p className="text-2xl font-black text-sprouts-text mt-1.5">20 September 2026</p>
            </div>
          </div>
        </section>

        {/* Guidelines */}
        <section className="mt-10">
          <h2 className="text-2xl font-extrabold text-sprouts-text">Guidelines</h2>
          <div className="mt-5 grid sm:grid-cols-2 gap-3">
            {RULES.map((r, i) => (
              <div
                key={i}
                className="flex items-start gap-3 bg-white rounded-xl px-4 py-3.5 border border-sprouts-border"
              >
                <CheckCircle2 size={20} className="text-sprouts-success shrink-0 mt-0.5" />
                <p className="text-sm font-semibold text-sprouts-text">{r}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Regional Champions */}
        <section className="mt-14" data-testid="regional-champions-section">
          <div className="flex flex-wrap items-center gap-2 mb-5">
            <Crown size={20} className="text-sprouts-yellow" />
            <h2 className="text-xl sm:text-2xl font-black text-sprouts-text tracking-wide">
              REGIONAL CHAMPIONS
            </h2>
            <span className="text-[10px] font-extrabold tracking-widest uppercase bg-sprouts-yellow/20 text-amber-700 px-2 py-1 rounded-full">
              Featured Tier
            </span>
          </div>
          <p className="text-sm text-sprouts-muted mb-5 max-w-3xl">
            One champion will be recognised from each participating region — the standout young
            travel storyteller representing their state on the national stage.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {REGIONAL_CHAMPIONS.map((region) => (
              <div
                key={region}
                className="relative overflow-hidden bg-gradient-to-br from-sprouts-slate to-slate-800 text-white rounded-2xl p-5 sm:p-6 border border-sprouts-yellow/30 shadow-card"
                data-testid={`rules-region-card-${region}`}
              >
                <Crown size={28} className="text-sprouts-yellow mb-3" />
                <p className="text-[11px] font-extrabold tracking-widest uppercase text-sprouts-yellow">
                  {region}
                </p>
                <p className="text-base sm:text-lg font-black mt-1 leading-tight">Champion</p>
                <p className="text-[11px] text-slate-300 font-bold mt-2">
                  Trophy • Certificate • Recognition • Cash Prize
                </p>
                <div className="absolute -right-6 -bottom-6 w-24 h-24 rounded-full bg-sprouts-yellow/5" />
              </div>
            ))}
          </div>
        </section>

        {/* Category Awards */}
        <section className="mt-14" data-testid="category-awards-section">
          <div className="flex items-center gap-2 mb-5">
            <Star size={18} className="text-sprouts-primary" />
            <h2 className="text-lg sm:text-xl font-black text-sprouts-text tracking-wide uppercase">
              Category Awards
            </h2>
          </div>
          <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {CATEGORY_AWARDS.map((p) => (
              <div
                key={p.title}
                className="bg-white rounded-2xl p-5 border-2 flex flex-col items-center text-center shadow-soft hover:shadow-card transition-shadow"
                style={{ borderColor: `${p.color}55` }}
              >
                <Trophy size={48} color={p.color} className="mb-2" />
                <p className="font-extrabold text-sm text-sprouts-text mb-2 leading-tight">
                  {p.title}
                </p>
                <span
                  className="text-[10px] font-extrabold px-2.5 py-1 rounded-full"
                  style={{ backgroundColor: `${p.color}22`, color: p.color }}
                >
                  Trophy • Certificate • Recognition • Cash Prize
                </span>
              </div>
            ))}
          </div>
        </section>

        {/* Awards Information */}
        <section className="mt-14" data-testid="rules-awards-information">
          <div className="flex items-center gap-2 mb-5">
            <Award size={20} className="text-sprouts-secondary" />
            <h2 className="text-xl sm:text-2xl font-black text-sprouts-text tracking-wide uppercase">
              Awards Information
            </h2>
          </div>
          <ul className="grid sm:grid-cols-2 gap-3">
            {[
              "Cash prizes up to \u20B95000 per category.",
              "Trophy & Certificate for winners by the Times NIE team.",
              "Awards ceremony details will be announced soon.",
              "Venue will be communicated after judging.",
            ].map((item) => (
              <li
                key={item}
                className="flex items-start gap-3 bg-white rounded-xl px-4 py-3.5 border border-sprouts-border"
              >
                <CheckCircle2 size={20} className="text-sprouts-success shrink-0 mt-0.5" />
                <p className="text-sm font-semibold text-sprouts-text">{item}</p>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </PageShell>
  );
}
